import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lock, ArrowRight, MessageCircle, TrendingUp, 
  Check, CheckCircle2, CreditCard, 
  DollarSign, Timer, Briefcase, Zap, Link as LinkIcon,
  X, FileSpreadsheet, Bell, Snowflake,
  FileText, MousePointer2, Download, UploadCloud, Copy, Sparkles, ShieldCheck, Star, Linkedin
} from 'lucide-react';
import { Button } from './ui/Button';
import { lockService } from '../services/lockService';

const LandingPage = () => {
  const navigate = useNavigate();
  const [demoUrl, setDemoUrl] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const hasTracked = useRef(false);

  // Tracking & SEO
  useEffect(() => {
    // Internal Tracker
    if (!hasTracked.current) {
        hasTracked.current = true;
        lockService.trackLandingView();
    }

    document.title = "Lockd.in | Turn content into leads";
    
    const updateMeta = (selector: string, attribute: string, value: string) => {
        let el = document.querySelector(selector);
        if (el) el.setAttribute(attribute, value);
    };

    const brandImage = "https://i.imgur.com/9pJAxCO.png";

    updateMeta('meta[name="description"]', 'content', "Turn any link or file into an email capture portal. Simple, aesthetic, and effective gating for creators.");
    updateMeta('meta[property="og:title"]', 'content', "Lockd.in | Gate your content, capture leads.");
    updateMeta('meta[property="og:description"]', 'content', "Stop building complex landing pages. Wrap your link or file in a secure Lock. Share it. Capture emails instantly.");
    updateMeta('meta[property="og:image"]', 'content', brandImage);
    updateMeta('meta[name="twitter:image"]', 'content', brandImage);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDemoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/login');
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-slate-900 selection:text-white overflow-x-hidden">
      
      {/* Floating Pill Navbar */}
      <div className="fixed top-6 left-0 right-0 flex justify-center z-50 px-4 pointer-events-none">
        <nav className={`pointer-events-auto flex items-center justify-between bg-white/70 backdrop-blur-xl border border-white/20 rounded-full shadow-xl shadow-slate-200/10 px-2 py-2 pl-6 w-full max-w-3xl transition-all duration-300 ${scrolled ? 'bg-white/90 shadow-2xl border-slate-200/50' : ''}`}>
             <div className="flex items-center gap-2 cursor-pointer mr-4 group" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/20 group-hover:scale-105 transition-transform">
                  <Lock className="w-5 h-5 text-white fill-white/20" />
                </div>
             </div>

             <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-500">
                 <button onClick={() => scrollToSection('method')} className="hover:text-slate-900 transition-colors">Method</button>
                 <button onClick={() => scrollToSection('features')} className="hover:text-slate-900 transition-colors">Features</button>
                 <button onClick={() => scrollToSection('preview')} className="hover:text-slate-900 transition-colors">Preview</button>
                 <button onClick={() => scrollToSection('pricing')} className="hover:text-slate-900 transition-colors">Pricing</button>
             </div>

             <div className="flex items-center gap-2 ml-auto">
                <button onClick={() => navigate('/login')} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hidden sm:block">Login</button>
                <Button onClick={() => navigate('/login')} className="h-10 px-6 rounded-full bg-[#10B981] hover:bg-[#059669] text-white text-sm border-none shadow-lg shadow-green-500/20">
                   Get Started
                </Button>
             </div>
        </nav>
      </div>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 text-center relative z-10">
        <motion.div 
          variants={container}
          initial="hidden"
          animate="show"
          className="max-w-4xl mx-auto"
        >
          
          {/* Social Proof - Realistic */}
          <motion.div variants={item} className="flex items-center justify-center gap-3 mb-8">
                <div className="flex -space-x-2">
                    {[12, 29, 43, 54].map((id, i) => (
                        <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-slate-100 overflow-hidden shadow-sm">
                            <img src={`https://i.pravatar.cc/150?img=${id}`} alt="User" className="w-full h-full object-cover" />
                        </div>
                    ))}
                </div>
                <div className="text-sm font-medium text-slate-600 bg-slate-100/50 px-3 py-1 rounded-full border border-slate-200/50">
                    Trusted by Early Adopters
                </div>
          </motion.div>

          <motion.h1 variants={item} className="text-6xl sm:text-8xl font-extrabold tracking-tighter text-slate-900 mb-8 leading-[0.95]">
            Turn any content into<br />
            <span className="text-slate-400">a lead magnet.</span>
          </motion.h1>
          
          <motion.p variants={item} className="text-xl text-slate-600 max-w-2xl mx-auto mb-12 leading-relaxed font-light">
            The simplest way to gate content. Wrap any link or file in a secure Lock. 
            Share it anywhere. Capture emails instantly.
          </motion.p>

          {/* Interactive Input */}
          <motion.div variants={item} className="max-w-lg mx-auto relative group">
            <div className="absolute -inset-0.5 bg-slate-200 rounded-2xl blur opacity-40 group-hover:opacity-70 transition duration-500"></div>
            <form onSubmit={handleDemoSubmit} className="relative flex items-center bg-white p-2 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100">
                <div className="pl-4 text-slate-400">
                    <LinkIcon className="w-5 h-5" />
                </div>
                <input 
                    type="text" 
                    value={demoUrl}
                    onChange={(e) => setDemoUrl(e.target.value)}
                    placeholder="Paste a link or drop files..." 
                    className="flex-1 h-12 px-4 bg-transparent border-none focus:ring-0 text-slate-900 placeholder-slate-300 text-base w-full"
                />
                <Button className="h-12 px-6 rounded-xl whitespace-nowrap bg-slate-900 hover:bg-black text-white">
                    Create Lock <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
            </form>
            <div className="mt-6 flex justify-center gap-6 text-xs font-medium text-slate-400">
                <span className="flex items-center"><CheckCircle2 className="w-3 h-3 mr-1.5 text-slate-900" /> Instant Setup</span>
                <span className="flex items-center"><CreditCard className="w-3 h-3 mr-1.5" /> No credit card</span>
            </div>
          </motion.div>

        </motion.div>
      </section>

      {/* PAIN CASE SECTION */}
      <section id="method" className="py-24 bg-slate-50 border-y border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
                <h2 className="text-3xl font-bold tracking-tight mb-4">The "Viral" Trap.</h2>
                <p className="text-slate-500 text-lg max-w-2xl mx-auto">
                    You write a great thread. You ask people to "Comment below". Then the nightmare begins.
                </p>
            </div>

            <div className="relative">
                <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-slate-200 -translate-y-1/2 z-0"></div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative z-10">
                    {/* Step 1 */}
                    <motion.div 
                        whileHover={{ y: -5 }}
                        className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm text-center relative group cursor-default"
                    >
                        <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6 text-blue-600 group-hover:scale-110 transition-transform duration-300">
                            <MessageCircle className="w-7 h-7" />
                        </div>
                        <div className="absolute top-6 right-6 text-slate-200 font-mono text-xs font-bold">01</div>
                        <h3 className="font-bold text-slate-900 mb-2 text-lg">The "Bait"</h3>
                        <p className="text-sm text-slate-500 leading-relaxed">
                            "Comment 'SCALE' and I'll DM you my 50-page guide!"
                        </p>
                    </motion.div>

                    {/* Step 2 */}
                    <motion.div 
                        whileHover={{ rotate: [0, -1, 1, -1, 1, 0], transition: { duration: 0.4 } }}
                        className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm text-center relative group cursor-default overflow-hidden"
                    >
                         <div className="absolute inset-0 bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative z-10">
                            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6 text-red-500 group-hover:bg-red-100 group-hover:text-red-600 transition-colors">
                                <Bell className="w-7 h-7 group-hover:animate-bounce" />
                            </div>
                            <div className="absolute top-0 right-0 text-slate-200 font-mono text-xs font-bold group-hover:text-red-200 transition-colors">02</div>
                            <h3 className="font-bold text-slate-900 mb-2 text-lg group-hover:text-red-600 transition-colors">The Chaos</h3>
                            <p className="text-sm text-slate-500 leading-relaxed group-hover:text-red-800/70">
                                500+ comments. You have to manually check who commented.
                            </p>
                            <div className="mt-4 inline-flex items-center text-xs font-semibold bg-red-100 text-red-600 px-3 py-1.5 rounded-full">
                                <X className="w-3 h-3 mr-1.5" /> 4hrs wasted
                            </div>
                        </div>
                    </motion.div>

                    {/* Step 3 */}
                    <motion.div 
                        whileHover={{ y: -5 }}
                        className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm text-center relative group cursor-default overflow-hidden"
                    >
                         <div className="absolute inset-0 opacity-0 group-hover:opacity-10 pointer-events-none transition-opacity duration-500" 
                              style={{ backgroundImage: 'linear-gradient(#cbd5e1 1px, transparent 1px), linear-gradient(90deg, #cbd5e1 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
                         </div>
                        <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-6 text-emerald-600 group-hover:scale-110 transition-transform">
                            <FileSpreadsheet className="w-7 h-7" />
                        </div>
                        <div className="absolute top-6 right-6 text-slate-200 font-mono text-xs font-bold">03</div>
                        <h3 className="font-bold text-slate-900 mb-2 text-lg">The Scrape</h3>
                        <p className="text-sm text-slate-500 leading-relaxed">
                            Scraping profiles. Guessing emails. Pasting into Excel. Uploading to CRM.
                        </p>
                    </motion.div>

                    {/* Step 4 */}
                    <motion.div 
                        whileHover={{ y: -5 }}
                        className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm text-center relative group cursor-default overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-sky-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <div className="relative z-10">
                            <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6 text-slate-500 group-hover:bg-sky-200 group-hover:text-sky-700 transition-colors">
                                <Snowflake className="w-7 h-7 group-hover:animate-pulse" />
                            </div>
                            <div className="absolute top-0 right-0 text-slate-200 font-mono text-xs font-bold">04</div>
                            <h3 className="font-bold text-slate-900 mb-2 text-lg group-hover:text-sky-900 transition-colors">The Delay</h3>
                            <p className="text-sm text-slate-500 leading-relaxed group-hover:text-sky-800/70">
                                You send the email 24h later. The lead is cold. They forgot who you are.
                            </p>
                        </div>
                    </motion.div>
                </div>

                <div className="mt-16 text-center">
                    <div className="inline-flex flex-col items-center animate-bounce">
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">There is a better way</span>
                        <div className="w-px h-12 bg-gradient-to-b from-slate-200 to-slate-400"></div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section id="features" className="py-24 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">Everything you need to convert.</h2>
            <p className="text-slate-500 text-lg">Powerful triggers to grow your audience and revenue.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
            
            {/* Viral Loops */}
            <motion.div 
                whileHover={{ y: -5 }}
                className="lg:col-span-8 bg-blue-50/50 rounded-[32px] p-8 sm:p-10 border border-blue-100/50 relative overflow-hidden group"
            >
                <div className="flex flex-col md:flex-row items-center justify-between gap-8 relative z-10">
                    <div className="max-w-md text-left">
                        <div className="w-12 h-12 bg-white border border-blue-100 rounded-2xl flex items-center justify-center text-blue-600 mb-6 shadow-sm">
                            <MessageCircle className="w-6 h-6" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">Viral Loops</h3>
                        <p className="text-slate-600 leading-relaxed">
                            Force users to <strong className="text-blue-700">comment and follow</strong> on your LinkedIn or X post to automatically unlock the link. No manual DMs. No spreadsheets.
                        </p>
                        <div className="flex items-center gap-4 mt-6">
                             <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-blue-100 text-[#0077b5] text-xs font-bold shadow-sm">
                                 <Linkedin className="w-4 h-4 fill-current" /> LinkedIn
                             </div>
                             <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-blue-100 text-black text-xs font-bold shadow-sm">
                                 <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path></svg>
                                 X (Twitter)
                             </div>
                        </div>
                    </div>
                    
                    <div className="w-full max-w-xs bg-white rounded-2xl border border-slate-200 p-5 shadow-xl shadow-blue-900/5">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-slate-100 rounded-full"></div>
                            <div className="h-3 w-24 bg-slate-100 rounded-full"></div>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 mb-4">
                            <div className="h-2 w-full bg-slate-200 rounded-full mb-2"></div>
                            <div className="h-2 w-2/3 bg-slate-200 rounded-full"></div>
                        </div>
                        <div className="flex justify-end">
                            <div className="bg-blue-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg shadow-blue-600/20">
                                <Check className="w-3 h-3" /> Comment Verified
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Tip Jar */}
            <motion.div 
                whileHover={{ y: -5 }}
                className="lg:col-span-4 bg-slate-900 text-white rounded-[32px] p-8 border border-slate-800 shadow-2xl relative overflow-hidden flex flex-col justify-between group"
            >
                 <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
                <div className="relative z-10">
                    <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-emerald-400 mb-6 border border-white/10">
                        <DollarSign className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Tip Jar & Paywalls</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">
                        Accept donations or charge a fixed fee to unlock your files.
                    </p>
                </div>
                <div className="mt-8 bg-slate-800/50 backdrop-blur-md p-5 rounded-2xl border border-slate-700/50 group-hover:border-emerald-500/30 transition-colors">
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Unlock Price</span>
                        <span className="text-xl font-bold text-white">$3.00</span>
                    </div>
                    <button className="w-full py-2.5 bg-white text-black rounded-xl font-bold text-xs flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors">
                         Apple Pay
                    </button>
                </div>
            </motion.div>

            {/* Scarcity */}
            <motion.div 
                whileHover={{ y: -5 }}
                className="lg:col-span-4 bg-rose-50/50 rounded-[32px] p-8 border border-rose-100/50 shadow-sm relative overflow-hidden group"
            >
                <div className="w-12 h-12 bg-white border border-rose-100 rounded-2xl flex items-center justify-center text-rose-500 mb-6">
                    <Timer className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Scarcity Mode</h3>
                <p className="text-slate-600 text-sm mb-6">
                    Add a countdown timer or limit the number of unlocks.
                </p>
                <div className="flex items-center justify-center bg-white text-rose-600 font-mono font-bold py-3 px-4 rounded-xl border border-rose-100 shadow-sm group-hover:scale-105 transition-transform">
                    02 : 14 : 59
                </div>
            </motion.div>

            {/* B2B Shield */}
            <motion.div 
                whileHover={{ y: -5 }}
                className="lg:col-span-4 bg-indigo-50/50 rounded-[32px] p-8 border border-indigo-100/50 shadow-sm relative overflow-hidden"
            >
                 <div className="w-12 h-12 bg-white border border-indigo-100 rounded-2xl flex items-center justify-center text-indigo-500 mb-6">
                    <Briefcase className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">B2B Shield</h3>
                <p className="text-slate-600 text-sm mb-6">
                    Enforce the use of professional (company domain) emails to obtain higher-quality leads.
                </p>
                <div className="flex items-center gap-3 text-xs bg-white p-3 rounded-xl border border-indigo-100 shadow-sm">
                    <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
                    <span className="text-slate-400 line-through decoration-slate-300">john@gmail.com</span>
                    <span className="ml-auto text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">BLOCKED</span>
                </div>
            </motion.div>

            {/* Real-time Data */}
            <motion.div 
                whileHover={{ y: -5 }}
                className="lg:col-span-4 bg-slate-50/80 rounded-[32px] p-8 border border-slate-200/60 shadow-sm relative overflow-hidden"
            >
                 <div className="w-12 h-12 bg-white border border-slate-200 rounded-2xl flex items-center justify-center text-slate-700 mb-6">
                    <TrendingUp className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Real-time Data</h3>
                <p className="text-slate-600 text-sm mb-8">
                    Track views, clicks, and conversion rates instantly.
                </p>
                <div className="flex items-end justify-between h-16 gap-2 px-2">
                    <div className="w-full bg-slate-200 rounded-t-lg h-[30%] group-hover:h-[40%] transition-all duration-500"></div>
                    <div className="w-full bg-slate-300 rounded-t-lg h-[60%] group-hover:h-[50%] transition-all duration-500 delay-75"></div>
                    <div className="w-full bg-slate-400 rounded-t-lg h-[45%] group-hover:h-[60%] transition-all duration-500 delay-100"></div>
                    <div className="w-full bg-slate-800 rounded-t-lg h-[90%] group-hover:h-[100%] transition-all duration-500 delay-150 shadow-lg shadow-slate-900/20"></div>
                    <div className="w-full bg-slate-500 rounded-t-lg h-[75%] group-hover:h-[80%] transition-all duration-500 delay-200"></div>
                </div>
            </motion.div>

          </div>
        </div>
      </section>

      <LiveDemoPreview />

      {/* PRICING SECTION */}
      <section id="pricing" className="py-24 bg-slate-50 border-t border-slate-200 relative overflow-hidden">
         <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
             <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-800 text-xs font-bold mb-6">
                 <Zap className="w-3 h-3 fill-current" /> BETA LAUNCH
             </div>
             <h2 className="text-4xl font-bold text-slate-900 mb-6">Simple pricing.<br/>Free while we build.</h2>
             <p className="text-slate-500 text-lg mb-12">
                 We are currently in public beta. All features are free to use. 
                 <br/>In the future, we will introduce premium plans for heavy usage.
             </p>

             <div className="bg-white rounded-3xl p-8 shadow-xl shadow-slate-200 border border-slate-100 max-w-sm mx-auto">
                 <div className="flex justify-center mb-4">
                     <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center">
                         <ShieldCheck className="w-8 h-8 text-white" />
                     </div>
                 </div>
                 <h3 className="text-2xl font-bold text-slate-900 mb-2">Early Adopter</h3>
                 <div className="text-4xl font-extrabold text-slate-900 mb-2">$0<span className="text-lg font-medium text-slate-400">/mo</span></div>
                 <p className="text-slate-400 text-sm mb-8">Unlimited access during beta.</p>

                 <div className="space-y-4 text-left mb-8">
                     <div className="flex items-center gap-3 text-sm text-slate-600">
                         <Check className="w-5 h-5 text-green-500 shrink-0" />
                         <span>Unlimited Locks</span>
                     </div>
                     <div className="flex items-center gap-3 text-sm text-slate-600">
                         <Check className="w-5 h-5 text-green-500 shrink-0" />
                         <span>Unlimited Leads</span>
                     </div>
                     <div className="flex items-center gap-3 text-sm text-slate-600">
                         <Check className="w-5 h-5 text-green-500 shrink-0" />
                         <span>Viral Loops & B2B Shield</span>
                     </div>
                     <div className="flex items-center gap-3 text-sm text-slate-600">
                         <Check className="w-5 h-5 text-green-500 shrink-0" />
                         <span>File Hosting</span>
                     </div>
                 </div>

                 <Button onClick={() => navigate('/login')} fullWidth className="h-12 bg-slate-900 text-white">
                     Start for Free
                 </Button>
             </div>
         </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2 group">
                <div className="w-8 h-8 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/20 group-hover:scale-105 transition-transform">
                    <Lock className="w-4 h-4 text-white fill-white/20" />
                </div>
                <span className="font-bold text-slate-900 text-sm tracking-tight">Lockd.in</span>
            </div>
            <div className="flex gap-8 text-sm text-slate-500 font-medium">
                <button onClick={() => navigate('/privacy')} className="hover:text-slate-900 transition-colors">Privacy</button>
                <button onClick={() => navigate('/terms')} className="hover:text-slate-900 transition-colors">Terms</button>
            </div>
            <div className="text-sm text-slate-400">
                © 2025 Lockd.in Inc.
            </div>
        </div>
      </footer>
    </div>
  );
};

const CreatorDemo = () => {
    const [step, setStep] = useState(0);
    const [cursorPos, setCursorPos] = useState({ x: '110%', y: '110%' });
    const [isClicking, setIsClicking] = useState(false);
    const [lockType, setLockType] = useState<'link' | 'file'>('link');
    const [files, setFiles] = useState<string[]>([]);
    const [b2bEnabled, setB2bEnabled] = useState(false);
    const [isPublished, setIsPublished] = useState(false);

    useEffect(() => {
        let timeouts: ReturnType<typeof setTimeout>[] = [];

        const runSequence = () => {
            // Reset
            setStep(0);
            setLockType('link');
            setFiles([]);
            setB2bEnabled(false);
            setIsPublished(false);
            setCursorPos({ x: '110%', y: '110%' });

            // 1. Move to File Type
            timeouts.push(setTimeout(() => {
                setStep(1);
                setCursorPos({ x: '75%', y: '18%' });
            }, 1000));

            // 2. Click File Type
            timeouts.push(setTimeout(() => {
                setStep(2);
                setIsClicking(true);
                setTimeout(() => {
                    setIsClicking(false);
                    setLockType('file');
                }, 200);
            }, 2000));

            // 3. Move to Dropzone
            timeouts.push(setTimeout(() => {
                setStep(3);
                setCursorPos({ x: '50%', y: '36%' });
            }, 3000));

            // 4. Upload File Simulation
            timeouts.push(setTimeout(() => {
                setStep(4);
                setFiles(['Strategy_Q1.pdf']);
            }, 3800));

            // 5. Move to B2B Toggle (Fixed Coordinate)
            timeouts.push(setTimeout(() => {
                setStep(5);
                setCursorPos({ x: '85%', y: '50%' });
            }, 4500));

            // 6. Click B2B
            timeouts.push(setTimeout(() => {
                setStep(6);
                setIsClicking(true);
                setTimeout(() => {
                    setIsClicking(false);
                    setB2bEnabled(true);
                }, 200);
            }, 5500));

             // 7. Move to Publish (Fixed Coordinate to hit bottom button)
             timeouts.push(setTimeout(() => {
                setStep(7);
                setCursorPos({ x: '50%', y: '91%' });
            }, 6500));

            // 8. Click Publish
            timeouts.push(setTimeout(() => {
                setStep(8);
                setIsClicking(true);
                setTimeout(() => {
                    setIsClicking(false);
                    setIsPublished(true);
                }, 200);
            }, 7500));

            // 9. Restart
            timeouts.push(setTimeout(() => {
                runSequence();
            }, 12000));
        };

        runSequence();
        return () => timeouts.forEach(clearTimeout);
    }, []);

    return (
        <div className="relative w-full max-w-sm mx-auto h-[480px] bg-white rounded-[32px] p-6 shadow-2xl border border-slate-100 flex flex-col overflow-hidden">
             {/* Header */}
             <div className="flex justify-between items-center mb-6">
                <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Creator View</div>
             </div>

             {/* Content */}
             <div className="space-y-4 flex-1 relative z-10">
                 {/* Type Toggle */}
                 <div className="flex bg-slate-100 p-1 rounded-xl">
                     <div className={`flex-1 py-1.5 text-center text-xs font-bold rounded-lg transition-all ${lockType === 'link' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400'}`}>Link</div>
                     <div className={`flex-1 py-1.5 text-center text-xs font-bold rounded-lg transition-all ${lockType === 'file' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400'}`}>File</div>
                 </div>

                 {/* Input Area */}
                 {lockType === 'link' ? (
                     <div className="h-24 bg-slate-50 rounded-xl border border-slate-200 flex items-center px-4 text-slate-400 text-xs">
                        https://...
                     </div>
                 ) : (
                     <div className="h-24 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center gap-2 transition-colors relative overflow-hidden">
                        {files.length > 0 ? (
                            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex flex-col items-center">
                                <FileText className="w-6 h-6 text-slate-900 mb-1" />
                                <span className="text-xs font-bold text-slate-900">{files[0]}</span>
                            </motion.div>
                        ) : (
                            <>
                                <UploadCloud className="w-6 h-6 text-slate-300" />
                                <span className="text-[10px] text-slate-400">Drop file here</span>
                            </>
                        )}
                        {/* Upload Success Flash */}
                        {files.length > 0 && (
                            <motion.div 
                                initial={{ opacity: 0 }} 
                                animate={{ opacity: [0, 0.5, 0] }} 
                                transition={{ duration: 0.5 }}
                                className="absolute inset-0 bg-green-100 pointer-events-none"
                            />
                        )}
                     </div>
                 )}

                 {/* Settings */}
                 <div className="space-y-3 pt-2">
                     <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-100">
                         <div className="flex items-center gap-2">
                             <div className={`w-6 h-6 rounded flex items-center justify-center ${b2bEnabled ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-200 text-slate-400'}`}>
                                 <Briefcase className="w-3 h-3" />
                             </div>
                             <span className="text-xs font-medium text-slate-600">B2B Only</span>
                         </div>
                         <div className={`w-8 h-4 rounded-full transition-colors relative ${b2bEnabled ? 'bg-indigo-600' : 'bg-slate-200'}`}>
                             <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-sm transition-transform ${b2bEnabled ? 'left-4.5 translate-x-4' : 'left-0.5'}`}></div>
                         </div>
                     </div>
                 </div>

                 {/* Publish Button */}
                 <div className="pt-4 mt-auto">
                     {isPublished ? (
                         <motion.div initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="h-10 bg-green-50 border border-green-100 rounded-xl flex items-center justify-center text-green-700 text-xs font-bold gap-2">
                             <Check className="w-3 h-3" /> Link Copied!
                         </motion.div>
                     ) : (
                        <div className="h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white text-xs font-bold gap-2 shadow-lg shadow-slate-900/20">
                            Publish Lock <Sparkles className="w-3 h-3" />
                        </div>
                     )}
                 </div>
             </div>

             {/* Cursor */}
             <motion.div
                initial={false}
                animate={{ 
                    top: cursorPos.y, 
                    left: cursorPos.x,
                    scale: isClicking ? 0.8 : 1
                }}
                transition={{ 
                    type: "spring", 
                    stiffness: 150, 
                    damping: 20, 
                    mass: 0.5 
                }}
                className="absolute z-50 pointer-events-none -ml-3 -mt-2"
                style={{ filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.3))' }}
            >
                <MousePointer2 className="w-6 h-6 text-slate-900 fill-slate-900 stroke-white stroke-2" />
                <div className="bg-slate-900 text-white text-[9px] px-2 py-0.5 rounded ml-4 -mt-2 whitespace-nowrap">
                    You (Creator)
                </div>
            </motion.div>
        </div>
    )
}

const VisitorDemo = () => {
    const [step, setStep] = useState(0);
    const [inputValue, setInputValue] = useState('');
    const fullEmail = "alex@growth.io";
    const [cursorPos, setCursorPos] = useState({ x: '90%', y: '90%' });
    const [isClicking, setIsClicking] = useState(false);

    useEffect(() => {
        let timeouts: ReturnType<typeof setTimeout>[] = [];
        
        const runSequence = () => {
            // Reset
            setStep(0);
            setInputValue('');
            setCursorPos({ x: '90%', y: '90%' });

            // 1. Move to Input (Target: 38% Y, 50% X)
            timeouts.push(setTimeout(() => {
                setStep(1);
                setCursorPos({ x: '50%', y: '38%' }); 
            }, 1000));

            // 2. Start Typing
            timeouts.push(setTimeout(() => {
                setStep(2);
            }, 1800));

            // Typing Loop
            let typeTime = 1800;
            for (let i = 0; i <= fullEmail.length; i++) {
                 timeouts.push(setTimeout(() => {
                     setInputValue(fullEmail.substring(0, i));
                 }, typeTime + (i * 50)));
            }
            typeTime += (fullEmail.length * 50) + 500;

            // 3. Move to Unlock Button (Target: 53% Y, 50% X)
            timeouts.push(setTimeout(() => {
                setStep(3);
                setCursorPos({ x: '50%', y: '53%' });
            }, typeTime));

            // 4. Click Unlock
            timeouts.push(setTimeout(() => {
                setStep(4);
                setIsClicking(true);
                setTimeout(() => setIsClicking(false), 200);
            }, typeTime + 800));

            // 5. Show Tip Jar (Friction)
            timeouts.push(setTimeout(() => {
                setStep(5);
                setCursorPos({ x: '50%', y: '48%' }); // Look at card
            }, typeTime + 1200));

            // 6. Hesitate then Move to No Thanks (Target: 65% Y, 50% X)
            timeouts.push(setTimeout(() => {
                setStep(6);
                setCursorPos({ x: '50%', y: '65%' }); 
            }, typeTime + 2500));

            // 7. Click No Thanks
            timeouts.push(setTimeout(() => {
                setStep(7);
                setIsClicking(true);
                setTimeout(() => setIsClicking(false), 200);
            }, typeTime + 3300));

            // 8. Success
            timeouts.push(setTimeout(() => {
                setStep(8);
            }, typeTime + 3600));

            // 9. Restart Loop
            timeouts.push(setTimeout(() => {
                runSequence();
            }, typeTime + 7000));
        };

        runSequence();

        return () => timeouts.forEach(clearTimeout);
    }, []);

    return (
        <div className="relative w-full max-w-sm mx-auto h-[480px]">
            {/* The Card */}
            <div className="bg-white/95 backdrop-blur-xl rounded-[32px] p-8 shadow-2xl shadow-black/50 relative z-20 border border-white/20 h-full flex flex-col">
                {/* Header Label */}
                <div className="absolute top-4 right-4">
                    <div className="bg-black/5 text-slate-400 text-[9px] px-2 py-1 rounded-full font-bold uppercase tracking-widest">
                        Visitor View
                    </div>
                </div>

                {/* Icon Notch */}
                <div className="flex justify-center -mt-16 mb-4">
                    <div className="bg-white p-4 rounded-full shadow-xl ring-8 ring-slate-900/50">
                        <FileText className="w-8 h-8 text-slate-900" />
                    </div>
                </div>

                    {/* FOMO Trigger */}
                <div className="flex justify-center mb-6">
                    <div className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-[10px] font-bold border border-red-100 flex items-center gap-1.5 animate-pulse">
                        <Timer className="w-3 h-3" />
                        Offer expires in 04:59
                    </div>
                </div>
                
                <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2 leading-tight">Q1 2025 Strategy.pdf</h3>
                    <p className="text-slate-500 text-sm">Enter your email to unlock this file.</p>
                </div>

                {/* DYNAMIC CONTENT AREA */}
                <div className="relative flex-1">
                    <AnimatePresence mode='wait'>
                        {/* STAGE: FORM */}
                        {step <= 4 && (
                            <motion.div 
                                key="form"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-4"
                            >
                                <div className="relative">
                                    <input 
                                        readOnly
                                        value={inputValue}
                                        placeholder="name@company.com"
                                        className="w-full h-14 bg-slate-100 rounded-2xl border-2 border-transparent px-5 text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-slate-900 transition-colors"
                                    />
                                </div>
                                <motion.div 
                                    animate={isClicking && step === 4 ? { scale: 0.95 } : { scale: 1 }}
                                    className="h-14 bg-slate-900 rounded-2xl flex items-center justify-center text-white font-semibold shadow-lg shadow-slate-900/20"
                                >
                                    Unlock File 🔓
                                </motion.div>
                            </motion.div>
                        )}

                        {/* STAGE: FRICTION / TIP JAR */}
                        {step >= 5 && step <= 7 && (
                            <motion.div 
                                key="friction"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-4"
                            >
                                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-center">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                        <DollarSign className="w-5 h-5 text-green-600" />
                                    </div>
                                    <h3 className="font-semibold text-slate-900 text-sm">Support the creator?</h3>
                                    <p className="text-xs text-slate-500 mb-3">
                                        Optional tip for this file.
                                    </p>
                                    <button className="w-full h-10 bg-black text-white rounded-xl font-medium text-xs flex items-center justify-center gap-2">
                                        Pay $3.00 <span className="bg-white/20 px-1 rounded">Apple Pay</span>
                                    </button>
                                    <div className="mt-3">
                                        <span className={`text-xs text-slate-400 underline decoration-slate-300 ${step >= 6 ? 'text-slate-600' : ''}`}>
                                            No thanks, just unlock
                                        </span>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* STAGE: SUCCESS */}
                        {step >= 8 && (
                            <motion.div 
                                key="success"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-center pt-4"
                            >
                                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900">Access Granted!</h3>
                                <Button className="mt-4 h-12 w-full bg-slate-900 text-white">
                                    <Download className="w-4 h-4 mr-2" /> Download PDF
                                </Button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="mt-auto pt-6 flex items-center justify-center gap-2 text-[10px] font-medium text-slate-400 uppercase tracking-wider">
                    <Lock className="w-3 h-3" /> Secure & Encrypted
                </div>
            </div>

            {/* SIMULATED CURSOR */}
            <motion.div
                initial={false}
                animate={{ 
                    top: cursorPos.y, 
                    left: cursorPos.x,
                    scale: isClicking ? 0.8 : 1
                }}
                transition={{ 
                    type: "spring", 
                    stiffness: 150, 
                    damping: 20, 
                    mass: 0.5 
                }}
                className="absolute z-50 pointer-events-none -ml-3 -mt-2"
                style={{ filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.3))' }}
            >
                <MousePointer2 className="w-8 h-8 text-slate-900 fill-slate-900 stroke-white stroke-2" />
                <div className="bg-emerald-500 text-white text-[9px] px-2 py-0.5 rounded ml-4 -mt-2 whitespace-nowrap">
                    Lead (Visitor)
                </div>
            </motion.div>

            {/* Decorative Background Cards */}
            <div className="absolute top-4 -right-4 w-full h-full bg-white/10 backdrop-blur-sm rounded-[32px] -z-10 rotate-3 border border-white/5"></div>
            <div className="absolute top-8 -right-8 w-full h-full bg-white/5 backdrop-blur-sm rounded-[32px] -z-20 rotate-6 border border-white/5"></div>
        </div>
    );
}

const LiveDemoPreview = () => {
    return (
      <section id="preview" className="py-32 bg-slate-900 relative overflow-hidden">
        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#10B981]/20 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center mb-20">
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">How it works.</h2>
                <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto">
                    You create a lock in seconds. Your leads get a seamless, premium experience.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
                {/* LEFT: Creator Flow */}
                <div className="flex flex-col items-center">
                    <CreatorDemo />
                    <div className="mt-8 text-center">
                        <div className="inline-block px-4 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-sm font-medium mb-2">Step 1</div>
                        <h3 className="text-white font-bold text-lg">Create & Configure</h3>
                        <p className="text-slate-500 text-sm mt-1">Upload files, enable B2B gating, and publish.</p>
                    </div>
                </div>

                {/* RIGHT: Visitor Flow */}
                <div className="flex flex-col items-center">
                    <VisitorDemo />
                    <div className="mt-8 text-center">
                         <div className="inline-block px-4 py-1.5 rounded-full bg-emerald-900/30 border border-emerald-800 text-emerald-400 text-sm font-medium mb-2">Step 2</div>
                        <h3 className="text-white font-bold text-lg">Capture & Convert</h3>
                        <p className="text-slate-500 text-sm mt-1">Visitors unlock content by providing verified info.</p>
                    </div>
                </div>
            </div>
        </div>
      </section>
    );
}

export default LandingPage;